import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalPage } from "../components/legal-page";

export const Route = createFileRoute("/legal/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy" },
      { name: "description", content: "How HR Service Centre collects, uses and protects personal data under UK GDPR and the Data Protection Act 2018." },
      { property: "og:title", content: "Privacy Policy" },
      { property: "og:url", content: "https://www.hrservicecentre.co.uk/legal/privacy" },
    ],
    links: [{ rel: "canonical", href: "https://www.hrservicecentre.co.uk/legal/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <LegalPage
      title="Privacy Policy"
      intro="This policy explains how HR Service Centre collects, uses and protects your personal data when you use this website and our services."
      lastReviewed="2026-09-14"
    >
      <h2>Who we are</h2>
      <p>
        HR Service Centre (powered by EmployerHQ), 20-22 Wenlock Road, London, N1 7GU ("we", "us") is the data
        controller for the personal data described in this policy. We are notified to the Information Commissioner's
        Office (ICO).
      </p>

      <h2>Data we collect</h2>
      <ul>
        <li>
          <strong>Enquiry and call-back forms</strong>, including our contact form, location call-back forms, industry
          enquiry forms and the "Ask an HR expert" widget: your name, email, phone, company, headcount, the topic you
          select and anything you write in the message field.
        </li>
        <li>
          <strong>Template downloads</strong>: name, email, company and company size, plus the template you requested.
        </li>
        <li>
          <strong>Briefing sign-ups</strong>: email address, and name and phone where you provide them.
        </li>
        <li>
          <strong>Consent records</strong>: the wording of the consent statement you agreed to, and the date and time
          you agreed, so we can evidence your permission.
        </li>
        <li>
          <strong>Technical and usage data</strong>: IP address, browser type, pages viewed and referring page.
        </li>
        <li>
          <strong>Analytics events</strong>: which pages, calculators and tools are used. These only run once you
          accept optional cookies.
        </li>
      </ul>

      <h2>How we use it</h2>
      <ul>
        <li>To answer your enquiry, arrange a call back or send you the template you asked for.</li>
        <li>To provide and administer any HR, employment law or health &amp; safety service you engage us for.</li>
        <li>To send briefings and updates where you have opted in, and only until you opt out.</li>
        <li>To keep the site secure, understand how it is used and improve our content and tools.</li>
      </ul>

      <h2>Lawful bases</h2>
      <p>We rely on the following lawful bases under UK GDPR:</p>
      <ul>
        <li><strong>Consent</strong>, for marketing emails and optional analytics cookies.</li>
        <li><strong>Contract</strong>, to deliver services you've engaged us for.</li>
        <li><strong>Legitimate interests</strong>, to respond to enquiries and improve our services.</li>
        <li><strong>Legal obligation</strong>, record-keeping, tax and regulatory requirements.</li>
      </ul>

      <h2>Who we share it with</h2>
      <p>
        We share personal data only with service providers who help us run this site and our business, such as our
        hosting, database, email and analytics providers, and with professional advisers where necessary. They act on
        our instructions and are bound by confidentiality. We do not sell your data or share it for third-party
        marketing.
      </p>

      <h2>How long we keep data</h2>
      <p>
        Enquiry and download data: up to 24 months from last contact. Client engagement records: 7 years after the
        engagement ends, in line with professional and tax obligations. Marketing data: until you opt out. Analytics
        data is retained in aggregate form.
      </p>

      <h2>Your rights</h2>
      <p>
        Under UK GDPR you have the right to access, rectify, erase, restrict or port your data, to object to
        processing, and to withdraw consent at any time. To exercise any of these, use our{" "}
        <Link to="/contact-hr-experts">contact form</Link> and tell us it is a data protection request, or write to us
        at the address above. We respond within one month. You can also complain to the ICO at{" "}
        <a href="https://ico.org.uk">ico.org.uk</a>.
      </p>

      <h2>International transfers</h2>
      <p>
        Where we use processors outside the UK (for example email or analytics providers), we rely on UK-approved
        safeguards such as the International Data Transfer Agreement or adequacy decisions.
      </p>

      <h2>Cookies</h2>
      <p>
        Our use of cookies is explained in our <Link to="/legal/cookies">Cookie Policy</Link>.
      </p>

      <h2>Contact</h2>
      <p>
        Data protection queries: send them through our{" "}
        <Link to="/contact-hr-experts">contact form</Link>, or write to HR Service Centre, 20-22 Wenlock Road, London,
        N1 7GU.
      </p>
    </LegalPage>
  );
}
